import TaskItemModal from '@/app/features/tasks/TaskItemModal.tsx';
import type { TaskLabelInfo, TaskPatch, TaskReminderCreate } from '@/app/features/tasks/types.ts';

import React from 'react';

import { kanbanService } from './service.ts';
import type {
  CardUpdate,
  KanbanCard,
  KanbanLabel,
  LabelCreate,
  LabelUpdate,
  PersonOption,
} from './types.ts';

interface Props {
  card: KanbanCard;
  boardLabels: KanbanLabel[];
  persons: PersonOption[];
  canEdit: boolean;
  highlightToken?: string;
  onClose: () => void;
  onUpdate: (cardId: string, data: CardUpdate) => Promise<void>;
  onToggleLabel: (
    cardId: string,
    labelId: string,
    currentLabelIds: string[],
  ) => Promise<void>;
  onSetReminders: (cardId: string, reminders: TaskReminderCreate[]) => Promise<void>;
  onCreateLabel: (data: LabelCreate) => Promise<KanbanLabel | null>;
  onUpdateLabel: (labelId: string, data: LabelUpdate) => Promise<void>;
  onDeleteLabel: (labelId: string) => Promise<void>;
  onReorderLabel: (orderedIds: string[]) => Promise<void>;
}

const KanbanCardModal: React.FC<Props> = ({
  card,
  boardLabels,
  persons,
  canEdit,
  highlightToken,
  onClose,
  onUpdate,
  onToggleLabel,
  onSetReminders,
  onCreateLabel,
  onUpdateLabel,
  onDeleteLabel,
  onReorderLabel,
}) => {
  const labels: TaskLabelInfo[] = boardLabels.map((l) => ({
    ...l,
    feature_instance_id: card.feature_instance_id,
  }));

  const handleUpdate = (id: string, patch: TaskPatch) =>
    onUpdate(id, patch as CardUpdate);

  const handleSetReminders = (_id: string, reminders: TaskReminderCreate[]) =>
    onSetReminders(card.id, reminders);

  const handleCreateLabel = async (data: {
    feature_instance_id: string;
    name: string;
    color: string;
  }): Promise<TaskLabelInfo | null> => {
    const created = await onCreateLabel(data as LabelCreate);
    if (!created) return null;
    return { ...created, feature_instance_id: card.feature_instance_id };
  };

  return (
    <TaskItemModal
      highlightToken={highlightToken}
      value={{
        id: card.id,
        feature_instance_id: card.feature_instance_id,
        title: card.title,
        size: card.size,
        due_date: card.due_date,
        assigned_person_id: card.assigned_person_id,
        document_content_html: card.document_content_html,
        label_ids: card.label_ids,
        reminders: card.reminders,
        force_on_dashboard: card.force_on_dashboard,
        created_by: card.created_by,
        updated_by: card.updated_by,
        created_at: card.created_at,
        updated_at: card.updated_at,
      }}
      labels={labels}
      persons={persons}
      canEdit={canEdit}
      canCreateLabel={canEdit}
      onClose={onClose}
      onUpdate={handleUpdate}
      fetchDocumentRevisions={() => kanbanService.listDocumentRevisions(card.id)}
      onToggleLabel={onToggleLabel}
      onSetReminders={handleSetReminders}
      onCreateLabel={handleCreateLabel}
      onUpdateLabel={onUpdateLabel}
      onDeleteLabel={onDeleteLabel}
      onReorderLabel={onReorderLabel}
    />
  );
};

export default KanbanCardModal;
